import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { Input } from './ui/input';
import ServiceToggle from './ServiceToggle';
import { Loader2, Link2, Mail, Ticket, Copy, Check, RotateCcw, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import TabPageLayout from './TabPageLayout';
import AutoSaveIndicator from './AutoSaveIndicator';

const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

/**
 * ReviewsTab — flat prod-polish surfaces (matches Branding/Waivers).
 * Three sections: Enable → Destination → Where to ask.
 */
const ReviewsTab = ({ onNavigate }) => {
  const [savingField, setSavingField] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [copied, setCopied] = useState(false);

  const [settings, setSettings] = useState({
    reviewsEnabled: false,
    reviewLinkUrl: '',
    includeOnTicket: true,
  });

  const orgSlug = localStorage.getItem('organizationSlug');
  const frontendUrl = window.location.origin;
  const defaultReviewUrl = `${frontendUrl}/${orgSlug || 'your-business'}/review`;

  const { data: orgData, isLoading, mutate } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  useEffect(() => {
    if (orgData) {
      setSettings({
        reviewsEnabled: orgData.reviewLinkEnabled || false,
        reviewLinkUrl: orgData.reviewLinkUrl || '',
        includeOnTicket: orgData.reviewOnTicketEnabled !== false,
      });
    }
  }, [orgData]);

  const saveSettings = async (newSettings) => {
    try {
      await apiV2.patch('/auth/organizations/me', {
        reviewLinkEnabled: newSettings.reviewsEnabled,
        reviewLinkUrl: newSettings.reviewLinkUrl || defaultReviewUrl,
        reviewOnTicketEnabled: newSettings.includeOnTicket,
      });
      mutate();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSavingField(null);
    }
  };

  const handleToggle = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    setSavingField(field);
    saveSettings(newSettings);
  };

  const handleUrlChange = (value) => setSettings(prev => ({ ...prev, reviewLinkUrl: value }));
  const handleUrlBlur = () => { setSavingField('reviewLinkUrl'); saveSettings(settings); };

  const resetToDefault = () => {
    const newSettings = { ...settings, reviewLinkUrl: defaultReviewUrl };
    setSettings(newSettings);
    setSavingField('reviewLinkUrl');
    saveSettings(newSettings);
    toast.success('Reset to BookingFlows review link');
  };

  const copyReviewLink = async () => {
    const linkToCopy = settings.reviewLinkUrl || defaultReviewUrl;
    try {
      await navigator.clipboard.writeText(linkToCopy);
      setCopied(true);
      toast.success('Review link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const isUsingCustomUrl = settings.reviewLinkUrl && settings.reviewLinkUrl !== defaultReviewUrl;
  const disabledClass = !settings.reviewsEnabled ? 'opacity-50 pointer-events-none' : '';

  return (
    <TabPageLayout
      title="Reviews"
      subtitle="Collect and display customer reviews"
      loading={isLoading}
      actions={<AutoSaveIndicator saving={!!savingField} savedFlash={savedFlash} />}
    >
      <div className="space-y-6">

        {/* Enable reviews */}
        <div className="card-soft p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="settings-section-title">Enable Reviews</h3>
              <p className="settings-section-subtitle">
                Customers with bookings in the last 90 days can leave a review.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-1">
              {savingField === 'reviewsEnabled' && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
              <ServiceToggle
                checked={settings.reviewsEnabled}
                onChange={(checked) => handleToggle('reviewsEnabled', checked)}
                data-testid="enable-reviews-toggle"
              />
            </div>
          </div>
        </div>

        {/* Review destination */}
        <div className="card-soft p-5 sm:p-6">
          <div className="mb-5">
            <h3 className="settings-section-title">Review Destination</h3>
            <p className="settings-section-subtitle">
              Where should customers leave reviews? Paste a Google or TripAdvisor link to redirect them there.
            </p>
          </div>

          <div className={`space-y-3 ${disabledClass}`}>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={settings.reviewLinkUrl || defaultReviewUrl}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  onBlur={handleUrlBlur}
                  placeholder={defaultReviewUrl}
                  className="pl-10 h-10"
                  disabled={!settings.reviewsEnabled}
                  data-testid="review-link-input"
                />
              </div>
              <button
                onClick={copyReviewLink}
                disabled={!settings.reviewsEnabled}
                className="px-3 h-10 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-md transition-colors disabled:opacity-50"
                data-testid="copy-review-link-btn"
                title="Copy link"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            {isUsingCustomUrl && (
              <button
                onClick={resetToDefault}
                disabled={!settings.reviewsEnabled}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium"
                data-testid="reset-to-default-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset to BookingFlows link
              </button>
            )}
          </div>
        </div>

        {/* Where to ask */}
        <div className="card-soft p-5 sm:p-6">
          <div className="mb-4">
            <h3 className="settings-section-title">Where We Ask</h3>
            <p className="settings-section-subtitle">
              Pick the surfaces that prompt customers for a review.
            </p>
          </div>

          <div className={`divide-y divide-gray-100 ${disabledClass}`}>
            {/* Ticket */}
            <div className="flex items-center justify-between py-3.5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center">
                  <Ticket className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Customer ticket</p>
                  <p className="text-xs text-gray-500">Review button on the booking confirmation page</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {savingField === 'includeOnTicket' && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                <ServiceToggle
                  checked={settings.includeOnTicket}
                  onChange={(checked) => handleToggle('includeOnTicket', checked)}
                  disabled={!settings.reviewsEnabled}
                  data-testid="include-on-ticket-toggle"
                />
              </div>
            </div>

            {/* Thank-you email — managed in Notifications */}
            <div className="flex items-center justify-between py-3.5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Thank-you email</p>
                  <p className="text-xs text-gray-500">
                    Sent after the experience with a review link included.
                  </p>
                </div>
              </div>
              {onNavigate ? (
                <button
                  onClick={() => onNavigate('notifications')}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                  data-testid="reviews-manage-thankyou-link"
                >
                  Manage in Notifications <ArrowRight className="w-3 h-3" />
                </button>
              ) : (
                <span className="text-xs text-gray-400">Managed in Notifications</span>
              )}
            </div>
          </div>
        </div>

      </div>
    </TabPageLayout>
  );
};

export default ReviewsTab;
